import React, { useState } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import {
	Alert,
	Button,
	Col,
	Modal,
	Popconfirm,
	Row,
	Select,
	Space,
	Typography,
	Upload,
	message,
} from 'antd';
import { createMenuMappingAxiosInstance } from 'createAxiosInstance';
import PropTypes from 'prop-types';

const ACTIONS = {
	import: {
		label: 'Upload new menu',
		brief: "Replace the app's staged menu with a CSV export.",
		detail:
			'The current menu rows for the selected app will be renamed to a ' +
			'pending-restore backup and the CSV contents will be imported in ' +
			'their place. Nothing is deleted until you confirm the import, and ' +
			'you can restore the previous menu at any time before confirming. ' +
			'Only one import can be pending at a time.',
		confirmTitle: 'Import this CSV and back up the current menu?',
	},
	confirm: {
		label: 'Confirm pending import',
		brief: 'Permanently delete the backup from the last import.',
		detail:
			'The pending-restore backup created by the last import will be ' +
			'permanently deleted and the imported menu becomes the only copy. ' +
			'This cannot be undone.',
		confirmTitle: 'Permanently delete the backup menu?',
	},
	restore: {
		label: 'Restore previous menu',
		brief: 'Roll back the last import.',
		detail:
			'The imported menu rows will be deleted and the pending-restore ' +
			'backup will be renamed back into place, returning the menu to its ' +
			'pre-import state.',
		confirmTitle: 'Discard the imported menu and restore the backup?',
	},
};

const APPS = [{ value: 'mixbowl', label: 'MixBowl' }];

MenuCsvImport.propTypes = {
	companyID: PropTypes.number.isRequired,
};

export default function MenuCsvImport({ companyID }) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [action, setAction] = useState(null);
	const [app, setApp] = useState('mixbowl');
	const [file, setFile] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const closeModal = () => {
		setIsModalOpen(false);
		setAction(null);
		setFile(null);
	};

	const submitDisabled =
		!action || (action === 'import' && !file) || isSubmitting;

	const onSubmit = async () => {
		setIsSubmitting(true);
		try {
			const axios = await createMenuMappingAxiosInstance(companyID);
			if (action === 'import') {
				const formData = new FormData();
				formData.append('source', app);
				formData.append('file', file);
				const response = await axios.post('menu/import', formData, {
					headers: { 'Content-Type': 'multipart/form-data' },
				});
				const counts = response?.data?.data;
				message.success(
					`Menu imported: ${counts?.main_items} items, ` +
						`${counts?.modifier_groups} modifier groups, ` +
						`${counts?.modifiers} modifiers. The previous menu is backed ` +
						'up until you confirm or restore.',
				);
			} else {
				await axios.post(`menu/import/${action}`, { source: app });
				message.success(
					action === 'confirm'
						? 'Import confirmed — the backup menu has been deleted.'
						: 'Previous menu restored.',
				);
			}
			closeModal();
		} catch (err) {
			message.error(err?.response?.data?.error || 'Menu import request failed');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<Row>
				<Col span={18}>
					<Typography.Text strong>
						Import/Restore 3PA Menu via CSV:
					</Typography.Text>{' '}
					Import a 3rd party app menu from a POS-exported CSV into the staging
					database, then confirm the import or restore the previous menu.
				</Col>
				<Col span={4}>
					<Button type="link" onClick={() => setIsModalOpen(true)}>
						Import/Restore 3PA Menu via CSV
					</Button>
				</Col>
			</Row>
			<Modal
				title="Import/Restore 3PA Menu via CSV"
				open={isModalOpen}
				onCancel={closeModal}
				footer={[
					<Button key="close" onClick={closeModal}>
						Close
					</Button>,
					<Popconfirm
						key="confirm"
						title={action && ACTIONS[action].confirmTitle}
						onConfirm={onSubmit}
						okText="Proceed"
						cancelText="Cancel"
						disabled={submitDisabled}
					>
						<Button type="primary" disabled={submitDisabled}>
							Confirm
						</Button>
					</Popconfirm>,
				]}
			>
				<Space direction="vertical" style={{ width: '100%' }}>
					<Select
						aria-label="Action"
						placeholder="Select an action"
						style={{ width: '100%' }}
						value={action}
						onChange={setAction}
						options={Object.entries(ACTIONS).map(([value, config]) => ({
							value,
							label: config.label,
							brief: config.brief,
						}))}
						optionRender={(option) => (
							<Space direction="vertical" size={0}>
								<Typography.Text>{option.data.label}</Typography.Text>
								<Typography.Text type="secondary">
									{option.data.brief}
								</Typography.Text>
							</Space>
						)}
					/>
					<Select
						aria-label="App"
						style={{ width: '100%' }}
						value={app}
						onChange={setApp}
						options={APPS}
					/>
					{action && (
						<Alert type="info" showIcon message={ACTIONS[action].detail} />
					)}
					{action === 'import' && (
						<Upload
							accept=".csv"
							maxCount={1}
							beforeUpload={() => false}
							fileList={file ? [file] : []}
							onChange={({ fileList }) =>
								setFile(fileList.length ? fileList[0].originFileObj : null)
							}
						>
							<Button icon={<UploadOutlined />}>Select CSV file</Button>
						</Upload>
					)}
				</Space>
			</Modal>
		</>
	);
}
